import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Nav } from '../icons/NavIcon';
import ProgressButton from './ProgressButton';

/**
 * LoginForm — the right-hand pane of the auth split.
 *
 * Owns:
 *   - controlled email + password inputs (preserving the same field
 *     names + validation as the previous Login screen so the existing
 *     auth API contract is untouched)
 *   - the submit-phase state machine that drives ProgressButton
 *     ('idle' → 'submitting' → 'finalizing' → 'success'|'error')
 *   - a slide-in error banner for failed sign-ins (rose tone) — kept
 *     local so it can sit *inside* the form chrome instead of relying
 *     on react-hot-toast (which still fires the success message).
 *
 * The actual auth + redirect lives in the parent Login page; this
 * component just calls `onSubmit({ email, password })` and reflects
 * the result through `submitState`.
 */
const PHASE_LABELS = {
  idle: 'Sign in',
  submitting: 'Signing you in…',
  finalizing: 'Loading your dashboard…',
  success: 'Welcome back',
  error: 'Try again',
};

export default function LoginForm({ onSubmit, submitState }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const errorRef = useRef(null);

  const phase = submitState?.phase || 'idle';
  const errorMessage = submitState?.error || '';

  useEffect(() => {
    if (errorMessage && errorRef.current) {
      errorRef.current.focus();
    }
  }, [errorMessage]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (phase === 'submitting' || phase === 'finalizing') return;
    // Read from the form DOM — browser autofill can overwrite the visible
    // password without syncing React state, or inject a stale saved password
    // that fights the dev pre-fill. FormData always matches what's submitted.
    const fd = new FormData(e.currentTarget);
    onSubmit({
      email: String(fd.get('email') || '').trim(),
      password: String(fd.get('password') || '').trim(),
    });
  };

  return (
    <section className="txnweb-login-form-pane" aria-label="Sign in">
      <div className="txnweb-login-form-card">
        <header className="txnweb-login-form-header">
          <h2 className="txnweb-login-form-heading">Sign in</h2>
          <p className="txnweb-login-form-subheading">
            Welcome back. Sign in to your txnweb.com account — your
            heatmaps, experiments, and performance signals are right
            where you left them.
          </p>
        </header>

        {errorMessage && (
          <div
            className="txnweb-login-form-error"
            role="alert"
            tabIndex={-1}
            ref={errorRef}
          >
            <Nav.alertCircle aria-hidden className="txnweb-login-form-error-icon" />
            <span>{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="txnweb-login-form" noValidate>
          <div>
            <label className="label" htmlFor="login-email">
              Email Address
            </label>
            <div className="relative">
              <Nav.mail
                aria-hidden
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-txnweb-muted pointer-events-none"
              />
              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field pl-10"
                placeholder="you@example.com"
                required
                disabled={phase === 'submitting' || phase === 'finalizing'}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <label className="label !mb-0" htmlFor="login-password">
                Password
              </label>
              <Link
                to="/forgot-password"
                className="text-[13px] font-semibold text-navy-700 hover:text-navy-800 underline-offset-2 hover:underline shrink-0"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <Nav.lock
                aria-hidden
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-txnweb-muted pointer-events-none"
              />
              <input
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field pl-10"
                placeholder="Enter your password"
                required
                disabled={phase === 'submitting' || phase === 'finalizing'}
              />
            </div>
          </div>

          <ProgressButton
            phase={phase}
            phaseLabels={PHASE_LABELS}
            trailingIcon={<Nav.arrowRight aria-hidden />}
            className="w-full"
          />
        </form>

        <div className="txnweb-login-form-footer">
          Don&apos;t have an account?{' '}
          <Link
            to="/register"
            className="text-navy-700 hover:text-navy-800 font-semibold underline-offset-2 hover:underline"
          >
            Create one
          </Link>
        </div>
      </div>
    </section>
  );
}

LoginForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  submitState: PropTypes.shape({
    phase: PropTypes.oneOf(['idle', 'submitting', 'finalizing', 'success', 'error']),
    error: PropTypes.string,
  }),
};
